"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  analyzeDecision,
  archiveDecision,
  chooseDecision,
  listDecisions,
  type Decision,
} from "@/lib/api";
import type { ReviewDecisionThread } from "@/lib/api/projection-types";
import DecisionThreadCard from "@/components/review/DecisionThreadCard";
import { useReviewProjection } from "@/hooks/useReviewProjection";
import { useDataRefresh } from "@/lib/store";
import { compactSurfaceTitle } from "@/lib/savedResourceTitles";
import { invalidateProjectionStores } from "@/lib/stores/projectionInvalidation";
import { useChatRevealHighlight } from "@/lib/useChatRevealHighlight";

type BusyAction = "analyze" | "choose" | "cancel";

const HEADER_STAT_ORDER = [
  { key: "open", label: "Open" },
  { key: "decided", label: "Decided" },
  { key: "cancelled", label: "Cancelled" },
] as const;

function buildDecisionIndex(decisions: Decision[]) {
  return new Map(decisions.map((decision) => [decision.id, decision]));
}

interface Props {
  onOpenNewDecision: () => void;
  refreshNonce?: number;
  refreshNotice?: string | null;
}

type ActiveDecisionThread = ReviewDecisionThread & {
  status: "exploring" | "leaning" | "decided";
};

export default function DecisionThreadsScreen({
  onOpenNewDecision,
  refreshNonce = 0,
  refreshNotice = null,
}: Props) {
  const { projection, status, error, refresh } = useReviewProjection();
  const [decisionDetails, setDecisionDetails] = useState<Decision[]>([]);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [busyState, setBusyState] = useState<{ threadId: string; action: BusyAction } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadDecisionDetails = useCallback(async () => {
    try {
      const response = await listDecisions("all");
      setDecisionDetails(response.decisions);
      setDetailError(null);
    } catch (loadError) {
      setDetailError(loadError instanceof Error ? loadError.message : "Failed to load decision details");
    }
  }, []);

  const syncAfterMutation = useCallback(async () => {
    invalidateProjectionStores(["review"]);
    await Promise.all([refresh(), loadDecisionDetails()]);
  }, [loadDecisionDetails, refresh]);

  useEffect(() => {
    void loadDecisionDetails();
  }, [loadDecisionDetails]);

  useEffect(() => {
    if (refreshNonce <= 0) return;
    setNotice(refreshNotice || "Added a new decision thread to Review.");
    void syncAfterMutation();
  }, [refreshNonce, refreshNotice, syncAfterMutation]);

  // Strict UAT 2026-05-24 — Chat Action Contract: a chat-created
  // decision fires notifyGoalChange via notifyFromActionResult
  // (decisions surface uses the goal-change notifier as proxy until
  // a dedicated decision-change notifier ships). Subscribe here so
  // the Decisions UI re-fetches automatically — without this, the
  // strict UAT P*-D14-LIBRARY-DECISION check never saw the new
  // decision card after chat created it.
  const lastGoalChange = useDataRefresh((s) => s.lastGoalChange);
  useEffect(() => {
    if (!lastGoalChange) return;
    void syncAfterMutation();
  }, [lastGoalChange, syncAfterMutation]);

  useEffect(() => {
    if (!notice) return;
    const timeoutId = window.setTimeout(() => setNotice(null), 4200);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  const decisionIndex = useMemo(() => buildDecisionIndex(decisionDetails), [decisionDetails]);
  const threads = projection?.decisions ?? [];
  const activeThreads = useMemo(
    () =>
      threads.filter(
        (thread): thread is ActiveDecisionThread => thread.status !== "revisit"
      ),
    [threads]
  );
  const chatRevealDecisionsKey = useMemo(
    () => activeThreads.map((thread) => [thread.id, ...thread.source_decision_ids].join(":")).join("|"),
    [activeThreads],
  );
  useChatRevealHighlight("decisions", chatRevealDecisionsKey);
  const headerStats = useMemo(() => {
    const counts: Record<(typeof HEADER_STAT_ORDER)[number]["key"], number> = {
      open: 0,
      decided: 0,
      cancelled: 0,
    };

    for (const thread of threads) {
      if (thread.status === "revisit") {
        counts.cancelled += 1;
      } else if (thread.status === "decided") {
        counts.decided += 1;
      } else {
        counts.open += 1;
      }
    }

    return HEADER_STAT_ORDER.map((item) => ({
      ...item,
      count: counts[item.key],
    }));
  }, [threads]);

  const threadDecisionMap = useMemo(() => {
    const map = new Map<string, Decision | null>();
    for (const thread of activeThreads) {
      const detail = thread.source_decision_ids
        .map((id) => decisionIndex.get(id))
        .find((decision): decision is Decision => Boolean(decision)) ?? null;
      map.set(thread.id, detail);
    }
    return map;
  }, [activeThreads, decisionIndex]);

  const handleAnalyze = useCallback(
    async (thread: ReviewDecisionThread, decision: Decision | null) => {
      if (!decision) return;
      setBusyState({ threadId: thread.id, action: "analyze" });
      try {
        await analyzeDecision(decision.id);
        await syncAfterMutation();
        setExpandedThreadId(thread.id);
        setNotice(`Updated the recommendation for ${compactSurfaceTitle(thread.topic, 60)}.`);
      } catch (actionError) {
        setNotice(
          actionError instanceof Error
            ? actionError.message
            : `Could not analyze ${compactSurfaceTitle(thread.topic, 60)} yet.`
        );
      } finally {
        setBusyState(null);
      }
    },
    [syncAfterMutation]
  );

  const handleChoose = useCallback(
    async (thread: ReviewDecisionThread, decision: Decision | null, optionName: string) => {
      if (!decision) return;
      setBusyState({ threadId: thread.id, action: "choose" });
      try {
        await chooseDecision(decision.id, optionName);
        await syncAfterMutation();
        setExpandedThreadId(thread.id);
        setNotice(
          `Marked ${optionName} as the current choice for ${compactSurfaceTitle(thread.topic, 60)}.`
        );
      } catch (actionError) {
        setNotice(
          actionError instanceof Error
            ? actionError.message
            : `Could not decide ${compactSurfaceTitle(thread.topic, 60)} yet.`
        );
      } finally {
        setBusyState(null);
      }
    },
    [syncAfterMutation]
  );

  const handleCancelThread = useCallback(
    async (thread: ReviewDecisionThread) => {
      setBusyState({ threadId: thread.id, action: "cancel" });
      try {
        await Promise.all(thread.source_decision_ids.map((id) => archiveDecision(id)));
        await syncAfterMutation();
        setExpandedThreadId((current) => (current === thread.id ? null : current));
        setNotice(
          `Cancelled ${compactSurfaceTitle(thread.topic, 60)}. It is no longer competing in open decisions.`
        );
      } catch (actionError) {
        setNotice(
          actionError instanceof Error
            ? actionError.message
            : `Could not cancel ${compactSurfaceTitle(thread.topic, 60)} yet.`
        );
      } finally {
        setBusyState(null);
      }
    },
    [syncAfterMutation]
  );

  if ((status === "loading" || status === "idle") && !projection) {
    return (
      <div className="flex justify-center py-14">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-forest/25 border-t-forest-med" />
      </div>
    );
  }

  if (status === "error" && !projection) {
    return (
      <div className="rounded-[28px] border border-amber/25 bg-card px-5 py-6 text-center">
        <p className="text-lg font-semibold text-text">Review is not ready yet</p>
        <p className="mt-2 text-sm text-text-light">{error || "Failed to load decision threads."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {notice && (
        <div
          data-testid="decision-thread-notice"
          className="rounded-2xl border border-sage/40 bg-sage-pale/60 px-4 py-3 text-sm text-forest-med"
        >
          {notice}
        </div>
      )}

      {/* Decide sub-tab header — eyebrow + h2 + text-forest matches the
          template established by BacklogScreen ("Library") so the four
          sub-tabs visually align. */}
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">
            Decisionease · Deciding
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-forest">Your open decisions</h2>
          <p className="mt-1 text-sm text-muted">
            Decide what still matters, and cancel what no longer deserves attention.
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenNewDecision}
          data-testid="new-decision-button"
          className="shrink-0 rounded-xl bg-forest-med px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest"
        >
          New decision
        </button>
      </div>

      <section
        data-testid="review-decision-stats"
        aria-label="Decision status summary"
        className="rounded-[28px] border border-border bg-card px-4 py-4"
      >
        <div className="grid gap-4 sm:grid-cols-3">
          {headerStats.map((stat) => (
            <div
              key={stat.key}
              data-testid={`review-decision-stat-${stat.key}`}
              className="rounded-2xl px-3 py-2 text-center"
            >
              <p className="text-[2rem] font-semibold leading-none text-text">{stat.count}</p>
              <p className="mt-2 text-xs tracking-[0.08em] text-muted">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {detailError && (
        <div className="rounded-2xl border border-amber/25 bg-sand-pale px-4 py-3 text-sm text-text-light">
          {detailError}
        </div>
      )}

      {activeThreads.length ? (
        <div className="space-y-4">
          {activeThreads.map((thread) => {
            const decision = threadDecisionMap.get(thread.id) ?? null;
            const busyAction = busyState?.threadId === thread.id ? busyState.action : null;
            return (
              <DecisionThreadCard
                key={thread.id}
                thread={thread}
                decision={decision}
                expanded={expandedThreadId === thread.id}
                busyAction={busyAction}
                onDecideNow={() => setExpandedThreadId(thread.id)}
                onCancel={() => void handleCancelThread(thread)}
                onAnalyze={() => void handleAnalyze(thread, decision)}
                onChoose={(optionName) => void handleChoose(thread, decision, optionName)}
              />
            );
          })}
        </div>
      ) : (
        <div className="rounded-[28px] border border-dashed border-border bg-card px-5 py-12 text-center">
          <p className="text-lg font-semibold text-text">No active decisions right now</p>
          <p className="mt-2 text-sm text-text-light">
            When a question is still pulling at you, add it here and keep one thread per topic.
          </p>
          <button
            type="button"
            onClick={onOpenNewDecision}
            data-testid="new-decision-button"
            className="mt-5 rounded-xl bg-forest-med px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest"
          >
            New decision
          </button>
        </div>
      )}
    </div>
  );
}
